<script lang="ts">
  import { settings, DEFAULTS, snapToUnits } from '../lib/state.svelte';
  import type { Units } from '../lib/units';
  import { altToM, altStep, mToAlt, type AltUnit, type DistUnit, type SpeedUnit } from '../lib/units';
  import Stepper from './Stepper.svelte';

  interface Props {
    onoffline: () => void;
    offlineStatus: string;
  }
  let { onoffline, offlineStatus }: Props = $props();

  const u = $derived(settings.units);
  const setUnits = (next: Units) => {
    settings.units = next;
    snapToUnits(settings);
  };
  const ALT: [AltUnit, string][] = [
    ['m', 'Metres'],
    ['ft', 'Feet'],
  ];
  const DIST: [DistUnit, string][] = [
    ['km', 'km'],
    ['mi', 'Miles'],
  ];
  const SPEED: [SpeedUnit, string][] = [
    ['kmh', 'km/h'],
    ['kt', 'knots'],
    ['ms', 'm/s'],
    ['mph', 'mph'],
  ];
</script>

<div class="settings">
  <h3>Units</h3>
  <div class="row">
    <span>Heights</span>
    <div class="seg" role="radiogroup" aria-label="Height unit">
      {#each ALT as [v, label] (v)}
        <button role="radio" aria-checked={u.alt === v} class:on={u.alt === v} onclick={() => setUnits({ ...u, alt: v })}>{label}</button>
      {/each}
    </div>
  </div>
  <div class="row">
    <span>Distances</span>
    <div class="seg" role="radiogroup" aria-label="Distance unit">
      {#each DIST as [v, label] (v)}
        <button role="radio" aria-checked={u.dist === v} class:on={u.dist === v} onclick={() => setUnits({ ...u, dist: v })}>{label}</button>
      {/each}
    </div>
  </div>
  <div class="row">
    <span>Wind and airspeed</span>
    <div class="seg" role="radiogroup" aria-label="Speed unit">
      {#each SPEED as [v, label] (v)}
        <button role="radio" aria-checked={u.speed === v} class:on={u.speed === v} onclick={() => setUnits({ ...u, speed: v })}>{label}</button>
      {/each}
    </div>
  </div>

  <h3>Safety</h3>
  <div class="row">
    <span>Margin above terrain<small>Routes must stay this high above the ground, and arrive this high.</small></span>
    <Stepper
      label="Safety margin"
      value={Math.round(mToAlt(settings.margin, u))}
      step={altStep(u) / 5}
      min={0}
      max={Math.round(mToAlt(500, u))}
      suffix=" {u.alt}"
      onchange={(v) => (settings.margin = altToM(v, u))}
    />
  </div>

  <details>
    <summary>Advanced</summary>
    <div class="row">
      <span>Grid detail<small>Finer is slower; try it on a fast phone.</small></span>
      <div class="seg" role="radiogroup" aria-label="Grid cell size">
        <button role="radio" aria-checked={settings.cell === 185} class:on={settings.cell === 185} onclick={() => (settings.cell = 185)}>Normal</button>
        <button role="radio" aria-checked={settings.cell === 90} class:on={settings.cell === 90} onclick={() => (settings.cell = 90)}>Fine</button>
      </div>
    </div>
    <div class="row">
      <span>Search radius</span>
      <Stepper
        label="Search radius"
        value={settings.radius / 1000}
        step={5}
        min={5}
        max={30}
        suffix=" km"
        onchange={(v) => (settings.radius = v * 1000)}
      />
    </div>
    <div class="row">
      <span>Margin ramp after launch<small>Right after launch you can't be a full margin above the slope; it grows to full over this distance.</small></span>
      <Stepper
        label="Margin ramp"
        value={Math.round(mToAlt(settings.launchRamp, u))}
        step={altStep(u)}
        min={0}
        max={Math.round(mToAlt(1000, u))}
        suffix=" {u.alt}"
        onchange={(v) => (settings.launchRamp = altToM(v, u))}
      />
    </div>
    <button class="btn link" onclick={() => (Object.assign(settings, { ...DEFAULTS, units: settings.units }), snapToUnits(settings))}>Reset settings to defaults</button>
  </details>

  <h3>Offline</h3>
  <p class="hint">Download maps and terrain for {settings.radius / 1000} km around the takeoff before you lose signal.</p>
  <button class="btn" onclick={onoffline}>Save this area for offline</button>
  {#if offlineStatus}<p class="hint">{offlineStatus}</p>{/if}
</div>

<style>
  h3 {
    margin: 14px 0 4px;
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--muted);
  }
  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 48px;
    flex-wrap: wrap;
  }
  .row > span {
    flex: 1;
    min-width: 120px;
    display: flex;
    flex-direction: column;
  }
  .row small {
    color: var(--muted);
    font-size: 12px;
  }
  details summary {
    padding: 12px 0 4px;
    cursor: pointer;
    font-weight: 600;
  }
</style>
